/**
 * Permission enum — the flat catalogue of every permission key.
 *
 * Per FIX_WAVE_DESIGN_DECISIONS.md D3 #5 (founder-locked 2026-05-15):
 *   "Permission strings codified as TypeScript const enum in @argus/shared.
 *    Backend AND frontend import the same enum. Compile-time consistency
 *    across API and UI."
 *
 * ─── What this is ─────────────────────────────────────────────────────────
 *
 * A flat, autocomplete-friendly catalogue of every `<action>:<resource>`
 * permission key the platform recognises. Each entry maps a SCREAMING_SNAKE
 * constant name to its canonical wire-format string — the same string a role
 * row stores in `roles.permissions`, the gate composes in
 * `requirePermission(action, resource)`, and the browser looks up in the
 * `permissions[]` that `GET /users/me` returns.
 *
 * ─── Why a flat catalogue? ────────────────────────────────────────────────
 *
 *   1. One auditable list of every permission the platform recognises.
 *   2. A typed constant call sites can import instead of magic-stringing.
 *   3. It is what the ROLES SCREEN renders. `PERMISSION_CATALOGUE` below
 *      turns it into `{ key, action, resource, label, group, highRisk }` rows,
 *      `GET /users/permissions/catalogue` serves them, and the permission
 *      picker draws every one — which is how a grant like `write:tools` stops
 *      being invisible on the screen that is supposed to grant it.
 *
 * ─── Coverage invariant ───────────────────────────────────────────────────
 *
 * B1-T3 changed what this checks. It used to cross-check against
 * `ROLE_PERMISSIONS`, the code matrix in `permissions.ts`; that matrix is
 * deleted and the five shipped bundles are ROWS
 * (`packages/api/src/db/seeds/roles.seed.ts`). The module-init invariant at
 * the bottom now asserts that every key here is a real
 * `RbacAction x RbacResource` pair placed in a group with a label — i.e. that
 * the catalogue endpoint can serve it and the picker can render it. The
 * converse ("every granted key is in this catalogue") is asserted where the
 * grants live, in `packages/api/src/db/__tests__/roles-seed-by-key.test.ts`.
 *
 * Adding a new permission therefore touches: the enums in `permissions.ts`
 * (if the action or resource is new), this catalogue, and `roles.seed.ts` for
 * the bundles that should hold it.
 *
 * ─── Out of scope ─────────────────────────────────────────────────────────
 *
 * - The SYSADMIN slate. The platform routes keep their own audience gate and
 *   `sysadminCan()` over `SYSADMIN_GRANTS` in `permissions.ts`.
 * - ABAC tenant_ownership scoping for `org_admin` (D3 #3).
 */

import {
  describePermission,
  PERMISSION_GROUP_ORDER,
  type PermissionCatalogueEntry,
  type RbacPermission,
} from './permissions.js';

/**
 * Canonical permission catalog.
 *
 * Each key is the SCREAMING_SNAKE rendering of the underlying
 * `<action>:<resource>` wire-format string with `:` / `.` / `-` collapsed
 * to `_`. Each value is the exact wire-format string usable as a Set key
 * in `ROLE_PERMISSIONS` lookups.
 *
 * Hand-authored (not generated) so a code reviewer can read down the list
 * and reason about it without running a build step. The module-init
 * invariant below catches typos and drift.
 *
 * `as const` makes every value a string-literal type, so
 * `typeof Permission[keyof typeof Permission]` is a precise union — not
 * just `string`. That union is exported below as the `Permission` type.
 */
export const Permission = {
  // ─── apply ────────────────────────────────────────────────────────────
  APPLY_FINE_TUNING: 'apply:fine_tuning',
  APPLY_IMPORT_EXPORT_BUNDLE_ARGUS_PACK: 'apply:import_export_bundle_argus_pack',

  // ─── delete ───────────────────────────────────────────────────────────
  DELETE_AGENTS: 'delete:agents',
  DELETE_ARGUS_LIVE: 'delete:argus_live',
  DELETE_AUDIT: 'delete:audit',
  DELETE_EVALUATION: 'delete:evaluation',
  DELETE_FINE_TUNING: 'delete:fine_tuning',
  DELETE_GROUPS: 'delete:groups',
  DELETE_HEALTH: 'delete:health',
  DELETE_INVITATIONS: 'delete:invitations',
  DELETE_LIBRARY: 'delete:library',
  // Foundations 1 / B1-T2 - the keys the four deleted role ladders and the
  // raw role compares carried. See the block comment on 'library.documents'
  // in permissions.ts for why documents are a separate resource from the
  // connector config.
  DELETE_LIBRARY_DOCUMENTS: 'delete:library.documents',
  DELETE_NOTIFICATIONS: 'delete:notifications',
  // ADR-0033 §8 D4 — Project Scope. Reserved for project deletion; admin+owner.
  DELETE_PROJECTS: 'delete:projects',
  // Pod-FINAL-CRIT #626 — Studio composable destructive grants.
  DELETE_PROMPTS: 'delete:prompts',
  DELETE_ROLES: 'delete:roles',
  DELETE_SECURITY_AUTH: 'delete:security.auth',
  DELETE_SECURITY_KEYS: 'delete:security.keys',
  DELETE_SECURITY_PRIVILEGED: 'delete:security.privileged',
  DELETE_SECURITY_SESSION: 'delete:security.session',
  DELETE_SECURITY_THREAT: 'delete:security.threat',
  DELETE_SETTINGS_API: 'delete:settings.api',
  DELETE_SETTINGS_INTEGRATIONS: 'delete:settings.integrations',
  // Pod-FINAL-CRIT #626 — Studio composable destructive grants.
  DELETE_SKILLS: 'delete:skills',
  DELETE_TENANTS: 'delete:tenants',
  // Pod-FINAL-CRIT #626 — Studio composable destructive grants.
  DELETE_TOOLS: 'delete:tools',
  DELETE_USERS: 'delete:users',
  // Fix wave 2 RBAC pod #857 — Automate workflows + playbooks delete grant.
  DELETE_WORKFLOWS: 'delete:workflows',

  // ─── export ───────────────────────────────────────────────────────────
  EXPORT_ARGUS_LIVE: 'export:argus_live',
  EXPORT_AUDIT: 'export:audit',
  EXPORT_FINE_TUNING: 'export:fine_tuning',
  EXPORT_PLATFORM_AUDIT: 'export:platform_audit',
  EXPORT_SETTINGS_DATA: 'export:settings.data',
  EXPORT_USERS: 'export:users',

  // ─── factory:reset ────────────────────────────────────────────────────
  FACTORY_RESET_SETTINGS_DATA: 'factory:reset:settings.data',
  FACTORY_RESET_TENANTS: 'factory:reset:tenants',

  // ─── identity-fields:write ────────────────────────────────────────────
  IDENTITY_FIELDS_WRITE_USERS: 'identity-fields:write:users',

  // ─── impersonate ──────────────────────────────────────────────────────
  // Foundations 2 / B2d-T2. The ONE gate on acting as another person;
  // decisions.md 2026-09-13 #2 deleted the per-organization switch that
  // used to sit beside it, so this key is the whole answer.
  IMPERSONATE_USERS: 'impersonate:users',

  // ─── invite ───────────────────────────────────────────────────────────
  INVITE_USERS: 'invite:users',

  // ─── kill ─────────────────────────────────────────────────────────────
  KILL_RUNTIME: 'kill:runtime',

  // ─── lifecycle:lock / lifecycle:unlock ────────────────────────────────
  LIFECYCLE_LOCK_ARGUS_LIVE: 'lifecycle:lock:argus_live',
  LIFECYCLE_LOCK_USERS: 'lifecycle:lock:users',
  LIFECYCLE_UNLOCK_ARGUS_LIVE: 'lifecycle:unlock:argus_live',
  LIFECYCLE_UNLOCK_USERS: 'lifecycle:unlock:users',

  // ─── manage ───────────────────────────────────────────────────────────
  MANAGE_ARGUS_FUNCTIONS: 'manage:argus.functions',
  MANAGE_ARGUS_MODELS: 'manage:argus.models',
  MANAGE_PLATFORM: 'manage:platform',
  MANAGE_SYSADMINS: 'manage:sysadmins',
  MANAGE_TENANTS: 'manage:tenants',

  // ─── members:manage ───────────────────────────────────────────────────
  MEMBERS_MANAGE_GROUPS: 'members:manage:groups',

  // ─── mfa:reset ────────────────────────────────────────────────────────
  MFA_RESET_USERS: 'mfa:reset:users',

  // ─── provision ────────────────────────────────────────────────────────
  PROVISION_TENANTS: 'provision:tenants',

  // ─── purge ────────────────────────────────────────────────────────────
  PURGE_MEMORY_OBSERVABILITY: 'purge:memory_observability',

  // ─── read ─────────────────────────────────────────────────────────────
  READ_AGENTS: 'read:agents',
  READ_ARGUS_ASSIGNMENTS: 'read:argus.assignments',
  READ_ARGUS_FUNCTIONS: 'read:argus.functions',
  READ_ARGUS_MODELS: 'read:argus.models',
  READ_ARGUS_TRUST: 'read:argus.trust',
  READ_ARGUS_LIVE: 'read:argus_live',
  READ_AUDIT: 'read:audit',
  READ_BRANDING: 'read:branding',
  READ_CONNECTIONS: 'read:connections',
  READ_EVALUATION: 'read:evaluation',
  READ_FINE_TUNING: 'read:fine_tuning',
  READ_GOVERNANCE: 'read:governance',
  READ_GROUPS: 'read:groups',
  READ_HEALTH: 'read:health',
  READ_INVITATIONS: 'read:invitations',
  READ_LIBRARY: 'read:library',
  READ_LIBRARY_DOCUMENTS: 'read:library.documents',
  READ_MCP: 'read:mcp',
  // ASK-M1 T3 (D1): my own memories on the Memories page — row-scoped by
  // user_id, so every shipped bundle holds it. write:memory / delete:memory
  // went with routes/v1/memory.ts, their only reader.
  READ_MEMORY: 'read:memory',
  READ_MEMORY_OBSERVABILITY: 'read:memory_observability',
  READ_NOTIFICATIONS: 'read:notifications',
  READ_PLATFORM: 'read:platform',
  READ_PLATFORM_AUDIT: 'read:platform_audit',
  READ_PLATFORM_GOVERNANCE: 'read:platform_governance',
  READ_PLATFORM_SETTINGS: 'read:platform_settings',
  // ADR-0033 §8 D4 — Project Scope. Every tenant role reads the project hub.
  READ_PROJECTS: 'read:projects',
  // Pod-FINAL-CRIT #626 — Studio composable read grants.
  READ_PROMPTS: 'read:prompts',
  READ_ROLES: 'read:roles',
  READ_RUNTIME: 'read:runtime',
  READ_SECURITY: 'read:security',
  READ_SECURITY_AUTH: 'read:security.auth',
  READ_SECURITY_KEYS: 'read:security.keys',
  READ_SECURITY_PRIVILEGED: 'read:security.privileged',
  READ_SECURITY_SESSION: 'read:security.session',
  READ_SECURITY_THREAT: 'read:security.threat',
  READ_SETTINGS: 'read:settings',
  READ_SETTINGS_API: 'read:settings.api',
  READ_SETTINGS_COMPLIANCE: 'read:settings.compliance',
  READ_SETTINGS_DATA: 'read:settings.data',
  READ_SETTINGS_FEATURES: 'read:settings.features',
  READ_SETTINGS_INTEGRATIONS: 'read:settings.integrations',
  // Pod-FINAL-CRIT #626 — Studio composable read grants.
  READ_SKILLS: 'read:skills',
  READ_SSO: 'read:sso',
  READ_SYSADMINS: 'read:sysadmins',
  READ_TENANTS: 'read:tenants',
  // Pod-FINAL-CRIT #626 — Studio composable read grants.
  READ_TOOLS: 'read:tools',
  READ_USERS: 'read:users',
  // Fix wave 2 RBAC pod #857 — Automate workflows + playbooks read grant.
  READ_WORKFLOWS: 'read:workflows',

  // ─── role:assign ──────────────────────────────────────────────────────
  ROLE_ASSIGN_USERS: 'role:assign:users',

  // ─── rotate ───────────────────────────────────────────────────────────
  ROTATE_SETTINGS_API: 'rotate:settings.api',

  // ─── share ────────────────────────────────────────────────────────────
  SHARE_ARGUS_LIVE: 'share:argus_live',
  SHARE_LIBRARY: 'share:library',

  // ─── sme_review ───────────────────────────────────────────────────────
  SME_REVIEW_FINE_TUNING: 'sme_review:fine_tuning',

  // ─── suspend ──────────────────────────────────────────────────────────
  SUSPEND_TENANTS: 'suspend:tenants',

  // ─── write ────────────────────────────────────────────────────────────
  WRITE_AGENTS: 'write:agents',
  WRITE_ARGUS_ASSIGNMENTS: 'write:argus.assignments',
  WRITE_ARGUS_FUNCTIONS: 'write:argus.functions',
  WRITE_ARGUS_MODELS: 'write:argus.models',
  WRITE_ARGUS_TRUST: 'write:argus.trust',
  WRITE_ARGUS_LIVE: 'write:argus_live',
  WRITE_AUDIT: 'write:audit',
  WRITE_BRANDING: 'write:branding',
  WRITE_CONNECTIONS: 'write:connections',
  WRITE_EVALUATION: 'write:evaluation',
  WRITE_FINE_TUNING: 'write:fine_tuning',
  WRITE_GOVERNANCE: 'write:governance',
  WRITE_GROUPS: 'write:groups',
  WRITE_HEALTH: 'write:health',
  WRITE_INVITATIONS: 'write:invitations',
  WRITE_LIBRARY: 'write:library',
  WRITE_LIBRARY_DOCUMENTS: 'write:library.documents',
  WRITE_APPROVALS: 'write:approvals',
  WRITE_INSIGHTS: 'write:insights',
  WRITE_WIKI: 'write:wiki',
  WRITE_MCP: 'write:mcp',
  WRITE_NOTIFICATIONS: 'write:notifications',
  WRITE_PLATFORM: 'write:platform',
  WRITE_PLATFORM_AUDIT: 'write:platform_audit',
  WRITE_PLATFORM_GOVERNANCE: 'write:platform_governance',
  WRITE_PLATFORM_SETTINGS: 'write:platform_settings',
  // ADR-0033 §8 D4 — Project Scope. Bind / unbind a project's sources + tools.
  WRITE_PROJECTS: 'write:projects',
  // Pod-FINAL-CRIT #626 — Studio composable write grants.
  WRITE_PROMPTS: 'write:prompts',
  WRITE_ROLES: 'write:roles',
  WRITE_RUNTIME: 'write:runtime',
  WRITE_SECURITY: 'write:security',
  WRITE_SECURITY_AUTH: 'write:security.auth',
  WRITE_SECURITY_KEYS: 'write:security.keys',
  WRITE_SECURITY_PRIVILEGED: 'write:security.privileged',
  WRITE_SECURITY_SESSION: 'write:security.session',
  WRITE_SECURITY_THREAT: 'write:security.threat',
  WRITE_SETTINGS: 'write:settings',
  WRITE_SETTINGS_API: 'write:settings.api',
  WRITE_SETTINGS_COMPLIANCE: 'write:settings.compliance',
  WRITE_SETTINGS_DATA: 'write:settings.data',
  WRITE_SETTINGS_FEATURES: 'write:settings.features',
  WRITE_SETTINGS_INTEGRATIONS: 'write:settings.integrations',
  // Pod-FINAL-CRIT #626 — Studio composable write grants.
  WRITE_SKILLS: 'write:skills',
  WRITE_SSO: 'write:sso',
  WRITE_SYSADMINS: 'write:sysadmins',
  WRITE_TENANTS: 'write:tenants',
  // Pod-FINAL-CRIT #626 — Studio composable write grants.
  WRITE_TOOLS: 'write:tools',
  WRITE_USERS: 'write:users',
  // Fix wave 2 RBAC pod #857 — Automate workflows + playbooks write grant.
  WRITE_WORKFLOWS: 'write:workflows',
} as const;

/**
 * Type-level enum: the union of every wire-format permission string in
 * the catalog. Use as the parameter type of helpers that accept a
 * pre-composed permission key (the existing two-arg `hasPermission` /
 * `requirePermission` API remains the canonical call shape until follow-
 * on PRs migrate call sites).
 */
export type Permission = typeof Permission[keyof typeof Permission];

/**
 * Type-level alias proving `Permission` is assignable to `RbacPermission`
 * (the `<action>:<resource>` template-literal type from permissions.ts).
 * If a Permission value ever diverges from the `RbacAction × RbacResource`
 * product, this assignment fails at compile time — the invariant below
 * catches the same drift at runtime.
 */
const _permissionTypecheck: Permission extends RbacPermission ? true : false = true;
void _permissionTypecheck;

/**
 * THE CATALOGUE, assembled: every permission key this release recognises,
 * with the label, group and risk flag a screen needs to render it.
 *
 * Foundations 1 (Users rail), B1-T3. `GET /users/permissions/catalogue`
 * serves exactly this array, and the Roles permission picker renders exactly
 * what it is served — the hand-mirrored `ALL_ACTIONS` / `ALL_RESOURCES` /
 * `RESOURCE_GROUPS` copies in `packages/web/src/routes/users/roles.tsx` and
 * `components/users/role-editor-dialog.tsx` are deleted, so a key that exists
 * cannot be invisible on the screen that grants it (the ledger's `write:tools`
 * defect).
 *
 * Sorted by group (declaration order in `PERMISSION_GROUPS`), then resource,
 * then action, so the wire order IS the render order and no screen sorts.
 */
export const PERMISSION_CATALOGUE: readonly PermissionCatalogueEntry[] = (() => {
  const groupRank = new Map<string, number>(
    PERMISSION_GROUP_ORDER.map((group, index) => [group, index] as const)
  );
  const entries: PermissionCatalogueEntry[] = [];
  for (const value of Object.values(Permission)) {
    const described = describePermission(value);
    // Unreachable: the invariant below refuses to load a catalogue with a key
    // `describePermission` cannot place. Kept as a type narrow, not a fallback.
    if (!described) continue;
    entries.push(described);
  }
  entries.sort((a, b) => {
    const ga = groupRank.get(a.group) ?? Number.MAX_SAFE_INTEGER;
    const gb = groupRank.get(b.group) ?? Number.MAX_SAFE_INTEGER;
    if (ga !== gb) return ga - gb;
    if (a.resource !== b.resource) return a.resource.localeCompare(b.resource);
    return a.action.localeCompare(b.action);
  });
  return Object.freeze(entries);
})();

/** Every catalogue key, for an O(1) "is this a key we recognise?" check. */
export const PERMISSION_KEYS: ReadonlySet<string> = new Set(
  PERMISSION_CATALOGUE.map((entry) => entry.key)
);

/**
 * THE TENANT SLICE — what a tenant surface may be told the vocabulary is.
 *
 * Review SEC-B1b-06. `GET /users/permissions/catalogue` is gated on
 * `read:roles`, which the shipped VIEWER bundle holds
 * (`db/seeds/roles.seed.ts`), so every signed-in member of every tenant could
 * read all 156 keys — including the 24 PLATFORM-audience keys that are the
 * sysadmin control surface (`manage:platform`, `write:platform`,
 * `export:platform_audit`, `purge:memory_observability`, `provision:tenants`,
 * …). No tenant principal can ever hold one: `PERMISSION_GROUPS` marks them
 * `audience: 'platform'`, the platform routes keep
 * `requireSysadmin`, and the role writer refuses the keys with 422
 * UNGRANTABLE_PERMISSION. A vocabulary nobody in the tenant can hold and no
 * tenant screen can act on is not part of the tenant's answer, and enumerating
 * the platform control surface to a Viewer is the leak.
 *
 * THE RULE, in one line: the tenant audience. That is the whole rule.
 *
 * IT USED TO HAVE AN EXCEPTION CLAUSE (prove PROVE-B1b-C2), and the exception
 * was covering for a mislabelled key:
 *
 *     entry.audience === 'tenant' || NON_DELEGABLE_PERMISSIONS.has(entry.key)
 *
 * `audience` was assigned per RESOURCE GROUP, and the `tenants` resource mixes
 * the platform's lifecycle actions with ONE action a tenant Owner performs on
 * its own tenant. So `factory:reset:tenants` — which the shipped OWNER bundle
 * genuinely holds (`packages/api/src/db/seeds/roles.seed.ts`) — was labelled
 * `'platform'`, and the clause existed to drag it back in. It dragged
 * `delete:tenants` in with it: a key NO shipped bundle carries and no tenant
 * role can ever hold, served to every tenant Viewer by this catalogue and by
 * `GET /admin/security/privileged/admin-roles` beside it, as pure noise in a
 * tenant picker.
 *
 * The fix is upstream, where the audience is declared: `PERMISSION_GROUPS`
 * carries a per-KEY `keyAudience` for the keys its group label is wrong for, so
 * `factory:reset:tenants` IS tenant-audience and `delete:tenants` stays
 * platform-audience. One declaration, one property, no second list — and
 * `NON_DELEGABLE_PERMISSIONS` goes back to being only what it says it is, the
 * rule about what a GRANT can confer (`findUngrantableGrants`, `can()`).
 *
 * It lives HERE, beside the catalogue it slices, for the reason
 * `NON_DELEGABLE_PERMISSIONS` does (review BF-2): the package that owns the
 * vocabulary owns what a key IS and who may be told about it. A route that
 * filtered would be a second catalogue with a different answer.
 */
export const TENANT_PERMISSION_CATALOGUE: readonly PermissionCatalogueEntry[] =
  Object.freeze(PERMISSION_CATALOGUE.filter((entry) => entry.audience === 'tenant'));

/** Every key the tenant slice carries, for an O(1) membership check. */
export const TENANT_PERMISSION_KEYS: ReadonlySet<string> = new Set(
  TENANT_PERMISSION_CATALOGUE.map((entry) => entry.key)
);

/**
 * THE SAME SLICE, applied to a list of keys a tenant surface is about to
 * report. Review SEC-B1e5-01.
 *
 * `TENANT_PERMISSION_CATALOGUE` answers "what is the tenant's vocabulary?" for
 * the picker. This answers the same question for a list that did NOT come from
 * the catalogue — today, the `sysadmin` slate (`SYSADMIN_GRANTS`) that
 * `GET /admin/security/privileged/admin-roles` renders as a synthetic row on a
 * TENANT screen. That row handed a tenant Admin all 34 platform-slate keys,
 * 24 of them PLATFORM audience (`manage:platform`, `provision:tenants`,
 * `export:platform_audit`, …), while the other catalogue route in the same
 * batch had already stopped serving exactly those keys. Two tenant surfaces
 * must not disagree about what a tenant may be told.
 *
 * It lives HERE and not in the route for the reason the slice itself does: a
 * `.filter()` written in a handler is a second catalogue with an answer of its
 * own, and the next handler would write a third. One rule, one place, every
 * caller.
 *
 * NOT a grant check and NOT a gate — `can()` / `sysadminCan()` decide what a
 * principal may DO. This decides only what a tenant surface may be TOLD, and
 * it narrows: an unknown key is dropped, never passed through
 * (principles.md #4).
 */
export function tenantVisibleKeys(keys: Iterable<string>): string[] {
  return [...keys].filter((key) => TENANT_PERMISSION_KEYS.has(key));
}

// ─── Module-load invariants ──────────────────────────────────────────────────
//
// B1-T3: these used to cross-check the flat catalogue against
// `ROLE_PERMISSIONS`, the code matrix. The matrix is gone — the five bundles
// are rows in the `roles` table (`db/seeds/roles.seed.ts`) — so what is
// checked here is the CATALOGUE itself: that every key is a real
// action x resource pair with a group and a label, which is what
// `GET /users/permissions/catalogue` promises and the picker renders.
//
// The other half of the old pair — "every granted key is in the catalogue" —
// is now asserted where the grants live: `roles-seed-by-key.test.ts` fails if
// a shipped bundle grants a key this catalogue does not carry.

(function assertPermissionEnumInvariants(): void {
  const seen = new Set<string>();
  for (const [enumName, value] of Object.entries(Permission)) {
    if (seen.has(value)) {
      throw new Error(
        `[permission-enum] Permission.${enumName} = '${value}' is a duplicate of another entry`
      );
    }
    seen.add(value);
    if (!describePermission(value)) {
      throw new Error(
        `[permission-enum] Permission.${enumName} = '${value}' is not a valid <action>:<resource> pair with a group in PERMISSION_GROUPS — it would be missing from the permission catalogue`
      );
    }
  }

  if (PERMISSION_CATALOGUE.length !== Object.keys(Permission).length) {
    throw new Error(
      `[permission-enum] The catalogue holds ${PERMISSION_CATALOGUE.length} entries for ${Object.keys(Permission).length} Permission values — a key was dropped`
    );
  }
})();
